import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./ResetPass.css";

const ResetPass = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [token, setToken] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Check if we have a token in the URL (for reset password form)
  useEffect(() => {
    // Check for token in URL parameters (/:token)
    const tokenFromParams = params.token;
    
    // Check for token in query parameters (?token=)
    const searchParams = new URLSearchParams(location.search);
    const tokenFromQuery = searchParams.get("token");
    
    // Use whichever token is available
    const tokenToUse = tokenFromParams || tokenFromQuery;
    
    if (tokenToUse) {
      setToken(tokenToUse);
      setShowResetForm(true);
      validateToken(tokenToUse);
    }
  }, [location, params]);

  // Validate token when it's present in URL
  const validateToken = async (tokenToValidate) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `https://api.swasthmitra.com/api/password/validate-token/${tokenToValidate}`
      );
      
      if (response.data.success) {
        setMessage("Please enter your new password");
        setIsSuccess(true);
      } else {
        setMessage("This password reset link is invalid or has expired");
        setIsError(true);
        setShowResetForm(false);
      }
    } catch (error) {
      setMessage("This password reset link is invalid or has expired");
      setIsError(true);
      setShowResetForm(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password form submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage("Please enter your email address");
      setIsError(true);
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      setIsError(false);
      
      const response = await axios.post(
        "https://api.swasthmitra.com/api/password/forgot-password",
        { email }
      );

      if (response.data.success) {
        setMessage("Password reset email sent! Please check your inbox.");
        setIsSuccess(true);
      }
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to send reset email. Please try again."
      );
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset password form submission
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      setIsError(true);
      return;
    }

    if (password.length < 6) {
      setMessage("Password must be at least 6 characters long");
      setIsError(true);
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      setIsError(false);
      
      const response = await axios.post(
        "https://api.swasthmitra.com/api/password/reset-password",
        { token, password }
      );

      if (response.data.success) {
        setMessage("Your password has been reset successfully!");
        setIsSuccess(true);
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to reset password. Please try again."
      );
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="resetPasswordSection">
        <h2>{showResetForm ? "Set New Password" : "Reset Your Password"}</h2>
        
        {message && (
          <div className={`message ${isError ? "error" : "success"}`}>
            {message}
          </div>
        )}
        
        <div className="resetPasswordContainer">
          {!showResetForm ? (
            // Forgot Password Form
            <>
              <p>We will send you an email to reset your password</p>
              <form onSubmit={handleForgotPassword}>
                <input
                  type="email"
                  placeholder="Email address *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button style={{marginTop:"10px"}} type="submit" disabled={isLoading}>
                  {isLoading ? "Sending..." : "Submit"}
                </button>
              </form>
            </>
          ) : (
            // Reset Password Form
            <>
              <p>Enter your new password</p>
              <form className="resetPasswordForm" onSubmit={handleResetPassword}>
                <input
                  type="password"
                  placeholder="New password *"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm new password *"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button type="submit" disabled={isLoading}>
                  {isLoading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>
        <p>
          Back to{" "}
          <Link to="/login">
            <span>Login</span>
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPass;
